/**
 * The touch trace: a ghost dot and a fading trail showing where the controller
 * is pointing.
 *
 * Coordinates are normalised so they survive different screen sizes, and the
 * vertical one is anchored to a segment rather than the viewport — otherwise the
 * dot would slide off the sentence it is pointing at as soon as new text
 * arrived or a viewer scrolled.
 *
 *   x  0..1 across the transcript column
 *   y  0..1 down the anchored segment, or down the viewport when `segmentId`
 *      is null (a finger not over any text)
 *
 * Pure maths and state, no canvas: the decay and projection rules are testable
 * on their own.
 */
import type { TouchPoint } from '@lt/protocol';
import type { SegmentBox, Viewport } from './scroll-anchor.js';

/** How long a trail point remains visible, in ms. */
export const TRAIL_LIFETIME_MS = 600;
/** Hard cap, so a long drag cannot grow the trail without bound. */
export const TRAIL_MAX_POINTS = 48;

export interface TracePoint {
  x: number;
  y: number;
  segmentId: string | null;
  at: number;
}

/** A trail point projected into this viewer's own pixel space. */
export interface ProjectedPoint {
  x: number;
  y: number;
  /** 1 at full strength, falling to 0 as the point ages. */
  opacity: number;
}

/**
 * Normalises a pointer position against the column and the segment under it.
 * Used by the controller before sending.
 */
export function normalisePointer(
  pointer: { x: number; y: number },
  column: { left: number; top: number; width: number; height: number },
  /** Segment boxes in content coordinates, plus the current scroll offset. */
  boxes: readonly SegmentBox[],
  scrollTop: number,
): Pick<TouchPoint, 'x' | 'y' | 'segmentId'> {
  const localX = pointer.x - column.left;
  const localY = pointer.y - column.top;
  const contentY = localY + scrollTop;

  const box = boxes.find((b) => contentY >= b.top && contentY < b.top + b.height);
  if (!box) {
    // Nothing under the finger: anchor X against the content column if known,
    // and fall back to viewport-relative.
    const defaultBox = boxes.length > 0 ? boxes[0] : null;
    const boxLeft = defaultBox?.left ?? 0;
    const boxWidth = defaultBox?.width ?? column.width;
    const withinX = boxWidth > 0 ? (localX - boxLeft) / boxWidth : 0;

    return {
      x: clamp01(withinX),
      y: clamp01(column.height > 0 ? localY / column.height : 0),
      segmentId: null,
    };
  }

  const boxLeft = box.left ?? 0;
  const boxWidth = box.width ?? column.width;
  const withinX = boxWidth > 0 ? (localX - boxLeft) / boxWidth : 0;
  const withinY = box.height > 0 ? (contentY - box.top) / box.height : 0;

  return {
    x: clamp01(withinX),
    y: clamp01(withinY),
    segmentId: box.id,
  };
}

/**
 * Projects a normalised point into this viewer's pixels.
 *
 * @returns null when the point is anchored to a segment this viewer does not
 *   have yet, or one scrolled out of view.
 */
export function projectPoint(
  point: TracePoint,
  column: { width: number; height: number },
  boxes: readonly SegmentBox[],
  viewport: Pick<Viewport, 'scrollTop'>,
  now: number,
): ProjectedPoint | null {
  const age = now - point.at;
  if (age > TRAIL_LIFETIME_MS) return null;
  const opacity = Math.max(0, 1 - age / TRAIL_LIFETIME_MS);

  if (point.segmentId === null) {
    const defaultBox = boxes.length > 0 ? boxes[0] : null;
    const boxLeft = defaultBox?.left ?? 0;
    const boxWidth = defaultBox?.width ?? column.width;
    const x = boxLeft + point.x * boxWidth;
    return { x, y: point.y * column.height, opacity };
  }

  const box = boxes.find((b) => b.id === point.segmentId);
  if (!box) return null;

  const boxLeft = box.left ?? 0;
  const boxWidth = box.width ?? column.width;
  const x = boxLeft + point.x * boxWidth;
  const y = box.top + point.y * box.height - viewport.scrollTop;
  // Off-screen points are dropped rather than drawn at the edge, which would
  // suggest the controller is pointing somewhere it is not.
  if (y < -box.height || y > column.height + box.height) return null;
  return { x, y, opacity };
}

/**
 * The trail state for one controller: newest point first, aged points dropped.
 */
export class TouchTrace {
  #points: TracePoint[] = [];
  #down = false;

  get isDown(): boolean {
    return this.#down;
  }

  /** The current pointer position, or null when the finger is up. */
  get head(): TracePoint | null {
    return this.#down ? (this.#points[0] ?? null) : null;
  }

  /** Newest first. */
  points(now = Date.now()): TracePoint[] {
    return this.#points.filter((p) => now - p.at <= TRAIL_LIFETIME_MS);
  }

  apply(point: TouchPoint, now = Date.now()): void {
    if (point.state === 'down') {
      // A fresh gesture starts a fresh trail rather than continuing the old one.
      this.#points = [];
      this.#down = true;
    }
    if (point.state === 'up') {
      this.#down = false;
      // The trail is kept so it can fade out instead of vanishing.
    } else {
      this.#down = true;
    }

    this.#points.unshift({ x: point.x, y: point.y, segmentId: point.segmentId, at: now });
    if (this.#points.length > TRAIL_MAX_POINTS) this.#points.length = TRAIL_MAX_POINTS;
  }

  /** Drops aged points. Called once per frame so the array cannot creep. */
  prune(now = Date.now()): void {
    this.#points = this.#points.filter((p) => now - p.at <= TRAIL_LIFETIME_MS);
    if (this.#points.length === 0) this.#down = false;
  }

  clear(): void {
    this.#points = [];
    this.#down = false;
  }
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}
